import { CONFIG, CONSUMER_TYPE_IDS, CONSUMER_TYPES, PRODUCER_TYPE_IDS, PRODUCER_TYPES, STORAGE_TYPES } from "./config";
import { type Axial, areAdjacent, edgeKey, hexKey, hexesInRadius } from "./hexgrid";
import { createRng, nextRandom, randomChoice, randomInRange } from "./rng";
import { linePairKey, solveGrid, touchingActiveLineIds } from "./solver";
import type { ConsumerNode, GameState, ProducerNode, StorageNode } from "./state";
import { phaseIndexOf } from "./state";
import { rollWeather } from "./weather";

export function createInitialGameState(seed: number): GameState {
  const rng = createRng(seed);
  const state: GameState = {
    turn: 0,
    rng,
    nodes: {},
    lines: {},
    lineBudgetKm: CONFIG.startingLineBudgetKm,
    controlNodeBudget: CONFIG.startingControlNodeBudget,
    batteryBudget: CONFIG.startingBatteryBudget,
    imbalanceAnger: 0,
    unsatisfiedAnger: 0,
    angerStreakDays: 0,
    dayHadAnger: false,
    score: 0,
    weatherToday: rollWeather(rng),
    weatherTomorrow: rollWeather(rng),
    gameOver: false,
    stats: {
      linesBuilt: 0,
      linesDismantledByPlayer: 0,
      linesBrokenBySaturation: 0,
      controlNodesBuilt: 0,
      batteriesBuilt: 0,
      totalMwServed: 0,
      totalImbalanceAngerGenerated: 0,
      totalUnsatisfiedAngerGenerated: 0,
      mwServedByProducerType: {},
      mwServedByConsumerType: {},
    },
  };
  spawnProducer(state);
  spawnConsumer(state);
  return state;
}

function isWithinMap(a: Axial): boolean {
  return Math.max(Math.abs(a.q), Math.abs(a.r), Math.abs(-a.q - a.r)) <= CONFIG.mapRadius;
}

function emptyHexCandidates(state: GameState): Axial[] {
  return hexesInRadius(CONFIG.mapRadius).filter((a) => !state.nodes[hexKey(a)]);
}

function spawnProducer(state: GameState): void {
  const candidates = emptyHexCandidates(state);
  if (candidates.length === 0) return;
  const chosen = randomChoice(state.rng, candidates);
  const typeId = randomChoice(state.rng, PRODUCER_TYPE_IDS);
  const type = PRODUCER_TYPES[typeId];
  const id = hexKey(chosen);
  const node: ProducerNode = {
    id,
    q: chosen.q,
    r: chosen.r,
    kind: "producer",
    typeId,
    capacityMw: randomInRange(state.rng, type.capacityMwMin, type.capacityMwMax),
    dial: type.defaultDialByPhase.slice(),
    // spawnProducer only ever runs at the day boundary (Morning, phase 0),
    // so starting the live value at that phase's default target means a
    // freshly-spawned plant needs no ramp-up lag to reach it. No prior
    // phase to derive a baseline from, so it starts as its own baseline.
    effectivePct: type.defaultDialByPhase[0],
    effectivePctBaseline: type.defaultDialByPhase[0],
    builtThisTurn: true,
  };
  state.nodes[id] = node;
}

function spawnConsumer(state: GameState): void {
  const candidates = emptyHexCandidates(state);
  if (candidates.length === 0) return;
  const chosen = randomChoice(state.rng, candidates);
  const typeId = randomChoice(state.rng, CONSUMER_TYPE_IDS);
  const type = CONSUMER_TYPES[typeId];
  const id = hexKey(chosen);
  const node: ConsumerNode = {
    id,
    q: chosen.q,
    r: chosen.r,
    kind: "consumer",
    typeId,
    capacityMw: randomInRange(state.rng, type.capacityMwMin, type.capacityMwMax),
  };
  state.nodes[id] = node;
}

function spawnDam(state: GameState): void {
  const candidates = emptyHexCandidates(state);
  if (candidates.length === 0) return;
  const chosen = randomChoice(state.rng, candidates);
  const id = hexKey(chosen);
  const type = STORAGE_TYPES.dam;
  const node: StorageNode = {
    id,
    q: chosen.q,
    r: chosen.r,
    kind: "storage",
    source: "dam",
    rateMw: type.rateMw,
    capacityMwh: type.capacityMwh,
    chargeMwh: type.capacityMwh / 2,
    mode: "auto",
    status: "active",
    dismantleTurnsLeft: 0,
    builtThisTurn: false,
  };
  state.nodes[id] = node;
}

/** Runs the phase about to elapse, then commits it and moves the turn
 * counter forward. Call `previewSolve` beforehand to show the player what
 * this would do before they commit to it. */
export function advanceTurn(state: GameState): void {
  if (state.gameOver) return;

  const phaseIndex = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
  const result = solveGrid(state, phaseIndex);

  if (result.totalImbalanceMw > 0) {
    state.imbalanceAnger += result.totalImbalanceMw * CONFIG.imbalanceAngerGainPerMw;
    state.stats.totalImbalanceAngerGenerated += result.totalImbalanceMw * CONFIG.imbalanceAngerGainPerMw;
    state.dayHadAnger = true;
  }
  if (result.totalUnsatisfiedMw > 0) {
    state.unsatisfiedAnger += result.totalUnsatisfiedMw * CONFIG.unsatisfiedAngerGainPerMw;
    state.stats.totalUnsatisfiedAngerGenerated += result.totalUnsatisfiedMw * CONFIG.unsatisfiedAngerGainPerMw;
    state.dayHadAnger = true;
  }
  state.imbalanceAnger = Math.max(0, state.imbalanceAnger - CONFIG.angerDecayPerTurn);
  state.unsatisfiedAnger = Math.max(0, state.unsatisfiedAnger - CONFIG.angerDecayPerTurn);

  const multiplier = Math.min(CONFIG.scoreMultiplierCap, 1 + state.angerStreakDays * CONFIG.scoreMultiplierPerStreakDay);
  state.score += result.totalServedMw * multiplier;
  state.stats.totalMwServed += result.totalServedMw;

  for (const [nodeId, served] of Object.entries(result.nodeServedMw)) {
    const node = state.nodes[nodeId];
    if (!node) continue;
    if (node.kind === "producer") {
      state.stats.mwServedByProducerType[node.typeId] = (state.stats.mwServedByProducerType[node.typeId] ?? 0) + served;
    } else if (node.kind === "consumer") {
      state.stats.mwServedByConsumerType[node.typeId] = (state.stats.mwServedByConsumerType[node.typeId] ?? 0) + served;
    }
  }

  for (const [storageId, delta] of Object.entries(result.storageChargeDeltaMwh)) {
    const node = state.nodes[storageId];
    if (!node || node.kind !== "storage") continue;
    node.chargeMwh = Math.max(0, Math.min(node.capacityMwh, node.chargeMwh + delta));
  }

  // Lines that saturated were still used successfully this phase; they
  // break starting next turn (design doc: "the line was still effectively
  // used successfully before it broke").
  for (const lineId of result.saturatedLineIds) {
    const line = state.lines[lineId];
    if (!line) continue;
    state.lineBudgetKm += line.lengthKm;
    state.stats.linesBrokenBySaturation += 1;
    delete state.lines[lineId];
  }

  // Progress player-initiated dismantles.
  for (const line of Object.values(state.lines)) {
    if (line.status !== "dismantling") continue;
    line.dismantleTurnsLeft -= 1;
    if (line.dismantleTurnsLeft <= 0) {
      state.lineBudgetKm += line.lengthKm;
      state.stats.linesDismantledByPlayer += 1;
      delete state.lines[line.id];
    }
  }
  for (const node of Object.values(state.nodes)) {
    if (node.kind !== "control" && !(node.kind === "storage" && node.source === "battery")) continue;
    if (node.status !== "dismantling") continue;
    node.dismantleTurnsLeft -= 1;
    if (node.dismantleTurnsLeft <= 0) {
      if (node.kind === "control") state.controlNodeBudget += CONFIG.controlNodeCost;
      else state.batteryBudget += CONFIG.batteryCost;
      delete state.nodes[node.id];
    }
  }

  if (state.imbalanceAnger >= CONFIG.angerGameOverThreshold || state.unsatisfiedAnger >= CONFIG.angerGameOverThreshold) {
    state.gameOver = true;
    return;
  }

  for (const line of Object.values(state.lines)) line.builtThisTurn = false;
  for (const node of Object.values(state.nodes)) {
    if (node.kind === "control" || node.kind === "producer" || (node.kind === "storage" && node.source === "battery")) {
      node.builtThisTurn = false;
    }
  }

  state.turn += 1;

  // Ramp every producer's live output toward the incoming phase's target —
  // every phase transition, uniformly (including Night→Morning, with no
  // special-casing for the day boundary), never instantly. See
  // ProducerNode.effectivePct/effectivePctBaseline and doc/game_design.md.
  const nextPhase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
  for (const node of Object.values(state.nodes)) {
    if (node.kind !== "producer") continue;
    const type = PRODUCER_TYPES[node.typeId];

    if (type.controlMode === "fixed") {
      // No inertia concept for an uncontrollable source — it's always
      // exactly whatever the pattern says for this phase (weather is
      // applied separately, in the solver), no ramp/baseline involved.
      node.effectivePct = node.dial[nextPhase];
      node.effectivePctBaseline = node.effectivePct;
      continue;
    }

    // The value carried in from the phase that just ended is this new
    // phase's baseline — frozen for the whole phase, so this same capped
    // move (and any player edits to this phase's dial afterward, in
    // setProducerDial) is always measured from here, not from a value
    // that's already partway ramped.
    const baseline = node.effectivePct;
    const target = node.dial[nextPhase];
    const delta = Math.max(-type.maxDialRampPerPhase, Math.min(type.maxDialRampPerPhase, target - baseline));
    node.effectivePct = baseline + delta;
    node.effectivePctBaseline = baseline;
  }

  if (nextPhase === 0) {
    state.angerStreakDays = state.dayHadAnger ? 0 : state.angerStreakDays + 1;
    state.dayHadAnger = false;

    state.lineBudgetKm += CONFIG.lineBudgetPerDayKm;
    const dayIndex = Math.floor(state.turn / CONFIG.phasesPerDay);
    if (dayIndex % CONFIG.controlNodeBudgetPerDays === 0) state.controlNodeBudget += CONFIG.controlNodeCost;
    if (dayIndex % CONFIG.batteryBudgetPerDays === 0) state.batteryBudget += CONFIG.batteryCost;

    spawnProducer(state);
    spawnConsumer(state);
    if (nextRandom(state.rng) < CONFIG.damSpawnChancePerDay) spawnDam(state);

    state.weatherToday = state.weatherTomorrow;
    state.weatherTomorrow = rollWeather(state.rng);
  }
}

/** Non-mutating look at what advanceTurn() would compute right now — used
 * for live feedback (flow visuals, "N lines will break") before the player
 * commits to "next turn". */
export function previewSolve(state: GameState) {
  return solveGrid(state, phaseIndexOf(state.turn, CONFIG.phasesPerDay));
}

// --- lines ---

export type BuildLineResult = "ok" | "not-adjacent" | "insufficient-budget" | "already-exists" | "out-of-bounds";

export function buildLine(state: GameState, a: Axial, b: Axial): BuildLineResult {
  if (!isWithinMap(a) || !isWithinMap(b)) return "out-of-bounds";
  if (!areAdjacent(a, b)) return "not-adjacent";
  const id = edgeKey(a, b);
  if (state.lines[id]) return "already-exists";
  const cost = CONFIG.lineCostPerHexEdgeKm;
  if (cost > state.lineBudgetKm) return "insufficient-budget";

  state.lineBudgetKm -= cost;
  state.lines[id] = {
    id,
    a: hexKey(a),
    b: hexKey(b),
    lengthKm: cost,
    status: "active",
    dismantleTurnsLeft: 0,
    builtThisTurn: true,
  };
  state.stats.linesBuilt += 1;
  return "ok";
}

/** Click on a line to toggle it: a same-turn build is cancelled instantly
 * (free undo), an established line starts a multi-turn dismantle, and a
 * line already dismantling comes back online instantly. */
export function toggleLine(state: GameState, lineId: string): void {
  const line = state.lines[lineId];
  if (!line) return;

  if (line.status === "active" && line.builtThisTurn) {
    state.lineBudgetKm += line.lengthKm;
    state.stats.linesBuilt -= 1;
    delete state.lines[lineId];
    return;
  }
  if (line.status === "active") {
    line.status = "dismantling";
    line.dismantleTurnsLeft = CONFIG.dismantleTurns;
    return;
  }
  if (line.status === "dismantling") {
    line.status = "active";
    line.dismantleTurnsLeft = 0;
  }
}

// --- control nodes ---

export type BuildNodeResult = "ok" | "occupied" | "insufficient-budget" | "out-of-bounds";

export function buildControlNode(state: GameState, hex: Axial): BuildNodeResult {
  if (!isWithinMap(hex)) return "out-of-bounds";
  const id = hexKey(hex);
  if (state.nodes[id]) return "occupied";
  if (CONFIG.controlNodeCost > state.controlNodeBudget) return "insufficient-budget";

  state.controlNodeBudget -= CONFIG.controlNodeCost;
  state.nodes[id] = {
    id,
    q: hex.q,
    r: hex.r,
    kind: "control",
    separatedPairs: [],
    status: "active",
    dismantleTurnsLeft: 0,
    builtThisTurn: true,
  };
  state.stats.controlNodesBuilt += 1;
  return "ok";
}

export function toggleControlNode(state: GameState, hexId: string): void {
  const node = state.nodes[hexId];
  if (!node || node.kind !== "control") return;

  if (node.status === "active" && node.builtThisTurn) {
    state.controlNodeBudget += CONFIG.controlNodeCost;
    state.stats.controlNodesBuilt -= 1;
    delete state.nodes[hexId];
    return;
  }
  if (node.status === "active") {
    node.status = "dismantling";
    node.dismantleTurnsLeft = CONFIG.dismantleTurns;
    return;
  }
  if (node.status === "dismantling") {
    node.status = "active";
    node.dismantleTurnsLeft = 0;
  }
}

/** Force every pair within `lineIds` (restricted to lines actually touching
 * this control node) to be electrically joined here, regardless of their
 * prior state — the "Join selected" UI action. Relationships involving
 * non-selected lines are untouched.
 *
 * Paired with `splitControlNodeLines` below, this lets the player reach an
 * arbitrary partition of the node's touching lines (e.g. {A,B} joined,
 * separate from {C,D,E} joined among themselves) in two actions instead of
 * having to toggle every cross pair individually — see doc/game_design.md's
 * control-node section. */
export function joinControlNodeLines(state: GameState, hexId: string, lineIds: string[]): void {
  const node = state.nodes[hexId];
  if (!node || node.kind !== "control") return;
  const touching = new Set(touchingActiveLineIds(state, hexId));
  const selected = lineIds.filter((id) => touching.has(id));

  for (let i = 0; i < selected.length; i++) {
    for (let j = i + 1; j < selected.length; j++) {
      const key = linePairKey(selected[i], selected[j]);
      const idx = node.separatedPairs.indexOf(key);
      if (idx >= 0) node.separatedPairs.splice(idx, 1);
    }
  }
}

/** Separate every selected line from every *non*-selected line touching
 * this control node — the "Split selected off" UI action. Relationships
 * within the selection, and within the rest, are left exactly as they were
 * (so splitting {A,B} off from {C,D,E} doesn't disturb an existing A-B or
 * C-D-E grouping). */
export function splitControlNodeLines(state: GameState, hexId: string, lineIds: string[]): void {
  const node = state.nodes[hexId];
  if (!node || node.kind !== "control") return;
  const touching = touchingActiveLineIds(state, hexId);
  const selected = new Set(lineIds.filter((id) => touching.includes(id)));

  for (const lineId of selected) {
    for (const otherId of touching) {
      if (selected.has(otherId)) continue;
      const key = linePairKey(lineId, otherId);
      if (!node.separatedPairs.includes(key)) node.separatedPairs.push(key);
    }
  }
}

// --- batteries ---

export function buildBattery(state: GameState, hex: Axial): BuildNodeResult {
  if (!isWithinMap(hex)) return "out-of-bounds";
  const id = hexKey(hex);
  if (state.nodes[id]) return "occupied";
  if (CONFIG.batteryCost > state.batteryBudget) return "insufficient-budget";

  state.batteryBudget -= CONFIG.batteryCost;
  const type = STORAGE_TYPES.battery;
  state.nodes[id] = {
    id,
    q: hex.q,
    r: hex.r,
    kind: "storage",
    source: "battery",
    rateMw: type.rateMw,
    capacityMwh: type.capacityMwh,
    chargeMwh: 0,
    mode: "auto",
    status: "active",
    dismantleTurnsLeft: 0,
    builtThisTurn: true,
  };
  state.stats.batteriesBuilt += 1;
  return "ok";
}

export function toggleBattery(state: GameState, hexId: string): void {
  const node = state.nodes[hexId];
  if (!node || node.kind !== "storage" || node.source !== "battery") return;

  if (node.status === "active" && node.builtThisTurn) {
    state.batteryBudget += CONFIG.batteryCost;
    state.stats.batteriesBuilt -= 1;
    delete state.nodes[hexId];
    return;
  }
  if (node.status === "active") {
    node.status = "dismantling";
    node.dismantleTurnsLeft = CONFIG.dismantleTurns;
    return;
  }
  if (node.status === "dismantling") {
    node.status = "active";
    node.dismantleTurnsLeft = 0;
  }
}

// --- storage mode ---

/** "auto" (default): the solver decides charge/discharge per its island's
 * own balance. "charge"/"discharge": a hard override — this storage
 * always does that, even against its island's own need. Applies to dams
 * as well as batteries; dams aren't player-built but their behavior is
 * still player-directable. */
export function setStorageMode(state: GameState, nodeId: string, mode: "auto" | "charge" | "discharge"): void {
  const node = state.nodes[nodeId];
  if (!node || node.kind !== "storage") return;
  node.mode = mode;
}

// --- producer dial ---

export function setProducerDial(state: GameState, nodeId: string, phaseIndex: number, valuePct: number): void {
  const node = state.nodes[nodeId];
  if (!node || node.kind !== "producer") return;
  const type = PRODUCER_TYPES[node.typeId];
  if (type.controlMode === "fixed") return; // not player-editable at all

  const clamped = Math.max(0, Math.min(100, Math.round(valuePct)));
  node.dial[phaseIndex] = clamped;

  // Editing the phase that's live *right now* also nudges the live value
  // immediately — same capped rule as the automatic per-transition ramp,
  // measured from this phase's frozen baseline (not the live value, which
  // may already have drifted from an earlier edit this same phase) so
  // repeated edits can't compound past maxDialRampPerPhase. Editing any
  // other phase just updates its target for whenever that phase begins.
  const currentPhase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
  if (phaseIndex === currentPhase) {
    if (node.builtThisTurn) {
      // Just appeared this phase (spawned or debug-placed) and hasn't
      // committed to anything yet — no real prior output to be "ramping"
      // away from, so it's freely settable, same free-undo window
      // lines/batteries/control nodes get before the next "Next Turn".
      // Keep the baseline in sync too, so a *second* edit this same phase
      // is equally free instead of suddenly being capped relative to the
      // first edit.
      node.effectivePct = clamped;
      node.effectivePctBaseline = clamped;
    } else {
      const delta = Math.max(
        -type.maxDialRampPerPhase,
        Math.min(type.maxDialRampPerPhase, clamped - node.effectivePctBaseline),
      );
      node.effectivePct = node.effectivePctBaseline + delta;
    }
  }
}

// --- debug tools (dev-only map editing — bypasses cost/budget/build delay
// entirely and never touches stats, so it can't be mistaken for real play
// on the end-game stats screen) ---

export type DebugPlaceSpec =
  | { kind: "producer"; typeId: string }
  | { kind: "consumer"; typeId: string }
  | { kind: "storage"; source: "dam" | "battery" }
  | { kind: "control" };

export type DebugPlaceResult = "ok" | "occupied" | "out-of-bounds";

export function debugPlaceNode(state: GameState, hex: Axial, spec: DebugPlaceSpec): DebugPlaceResult {
  if (!isWithinMap(hex)) return "out-of-bounds";
  const id = hexKey(hex);
  if (state.nodes[id]) return "occupied";

  if (spec.kind === "producer") {
    const type = PRODUCER_TYPES[spec.typeId];
    if (!type) return "occupied"; // unknown type id — treat as a no-op rather than corrupt state
    const phase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
    const node: ProducerNode = {
      id,
      q: hex.q,
      r: hex.r,
      kind: "producer",
      typeId: spec.typeId,
      capacityMw: randomInRange(state.rng, type.capacityMwMin, type.capacityMwMax),
      dial: type.defaultDialByPhase.slice(),
      // Settle immediately at whatever this phase's default target is,
      // rather than starting at phase 0's value and needing to ramp up —
      // debug placement can happen at any phase, unlike spawnProducer.
      effectivePct: type.defaultDialByPhase[phase],
      effectivePctBaseline: type.defaultDialByPhase[phase],
      builtThisTurn: true,
    };
    state.nodes[id] = node;
  } else if (spec.kind === "consumer") {
    const type = CONSUMER_TYPES[spec.typeId];
    if (!type) return "occupied";
    const node: ConsumerNode = {
      id,
      q: hex.q,
      r: hex.r,
      kind: "consumer",
      typeId: spec.typeId,
      capacityMw: randomInRange(state.rng, type.capacityMwMin, type.capacityMwMax),
    };
    state.nodes[id] = node;
  } else if (spec.kind === "storage") {
    const type = STORAGE_TYPES[spec.source];
    const node: StorageNode = {
      id,
      q: hex.q,
      r: hex.r,
      kind: "storage",
      source: spec.source,
      rateMw: type.rateMw,
      capacityMwh: type.capacityMwh,
      chargeMwh: spec.source === "dam" ? type.capacityMwh / 2 : 0,
      mode: "auto",
      status: "active",
      dismantleTurnsLeft: 0,
      builtThisTurn: false,
    };
    state.nodes[id] = node;
  } else {
    state.nodes[id] = {
      id,
      q: hex.q,
      r: hex.r,
      kind: "control",
      separatedPairs: [],
      status: "active",
      dismantleTurnsLeft: 0,
      builtThisTurn: false,
    };
  }
  return "ok";
}

/** Removes any node (producer/consumer/storage/control) instantly — no
 * refund, no dismantle delay, no stats tracking. Lines are untouched;
 * dismantle a line the normal way (Line mode) if needed. */
export function debugRemoveNode(state: GameState, hexId: string): void {
  delete state.nodes[hexId];
}

export function debugSetLineBudgetKm(state: GameState, km: number): void {
  state.lineBudgetKm = Math.max(0, Math.round(km));
}

export function debugSetControlNodeBudget(state: GameState, n: number): void {
  state.controlNodeBudget = Math.max(0, Math.round(n));
}

export function debugSetBatteryBudget(state: GameState, n: number): void {
  state.batteryBudget = Math.max(0, Math.round(n));
}

/** Directly overrides a producer's or consumer's nominal capacityMw —
 * bypasses the normal random-at-spawn sampling entirely, for testing a
 * specific scenario. No-op for any other node kind. */
export function debugSetNodeCapacity(state: GameState, nodeId: string, capacityMw: number): void {
  const node = state.nodes[nodeId];
  if (!node || (node.kind !== "producer" && node.kind !== "consumer")) return;
  node.capacityMw = Math.max(0, capacityMw);
}
